import { supabase } from "@/integrations/supabase/client";
import {
  CABINET_TYPE_DEFAULTS,
  ACCESSORIES,
  EXTRA_TYPES,
  WOOD_SPECIES_OPTIONS,
  type CabinetType,
} from "@/data/cabinetDefaults";
import { PRICING_CONSTANTS } from "./pricingConstants";

/**
 * Snapshot of the in-code defaults, captured at module load BEFORE any
 * settings have been applied. Used by the admin editor's "Reset to default"
 * action and to know whether a setting is overridden.
 */
export const CABINET_RATE_DEFAULTS: Record<string, number> = Object.fromEntries(
  Object.entries(CABINET_TYPE_DEFAULTS).map(([k, v]) => [k, v.pricePerLinearFt])
);
export const ACCESSORY_PRICE_DEFAULTS: Record<string, number> = Object.fromEntries(
  ACCESSORIES.map((a) => [a.id, a.price])
);
export const EXTRA_PRICE_DEFAULTS: Record<string, number> = Object.fromEntries(
  EXTRA_TYPES.map((e) => [e.id, e.pricePerUnit])
);
export const SPECIES_MULTIPLIER_DEFAULTS: Record<string, number> = Object.fromEntries(
  WOOD_SPECIES_OPTIONS.map((s) => [s.name, s.priceMultiplier])
);

// ── Key builders ──────────────────────────────────────────────────────
export const settingKey = {
  cabinet: (type: string) => `cabinet_type:${type}:pricePerLinearFt`,
  accessory: (id: string) => `accessory:${id}:price`,
  extra: (id: string) => `extra:${id}:pricePerUnit`,
  species: (name: string) => `species:${name}:priceMultiplier`,
  constant: (name: keyof typeof PRICING_CONSTANTS) => `constant:${name}`,
};

// ── Apply / Load ──────────────────────────────────────────────────────
function applyRow(key: string, value: unknown) {
  const parts = key.split(":");
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return;

  if (parts[0] === "cabinet_type" && parts[2] === "pricePerLinearFt") {
    const type = parts[1] as CabinetType;
    if (CABINET_TYPE_DEFAULTS[type]) {
      CABINET_TYPE_DEFAULTS[type].pricePerLinearFt = num;
    }
  } else if (parts[0] === "accessory" && parts[2] === "price") {
    const acc = ACCESSORIES.find((a) => a.id === parts[1]);
    if (acc) acc.price = num;
  } else if (parts[0] === "extra" && parts[2] === "pricePerUnit") {
    const ex = EXTRA_TYPES.find((e) => e.id === parts[1]);
    if (ex) ex.pricePerUnit = num;
  } else if (parts[0] === "species" && parts[2] === "priceMultiplier") {
    const sp = WOOD_SPECIES_OPTIONS.find((s) => s.name === parts[1]);
    if (sp) sp.priceMultiplier = num;
  } else if (parts[0] === "constant" && parts[1] in PRICING_CONSTANTS) {
    (PRICING_CONSTANTS as Record<string, number>)[parts[1]] = num;
  }
}

let loadPromise: Promise<void> | null = null;
let isLoaded = false;

export function isPricingLoaded() {
  return isLoaded;
}

export async function loadPricingSettings(force = false): Promise<void> {
  if (loadPromise && !force) return loadPromise;
  const run = (async () => {
    // pricing_settings is readable by signed-in users only; skip the request
    // entirely for anonymous visitors (public marketing pages don't need it).
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      loadPromise = null;
      return;
    }
    const { data, error } = await supabase
      .from("pricing_settings")
      .select("key,value");
    if (error) {
      console.warn("[pricingSettings] failed to load:", error.message);
      return;
    }
    for (const row of data ?? []) {
      applyRow(row.key, (row as any).value);
    }
    isLoaded = true;
  })();
  loadPromise = run;
  return run;
}

export async function fetchAllPricingSettings(): Promise<Map<string, number>> {
  const map = new Map<string, number>();
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) return map;
  const { data, error } = await supabase
    .from("pricing_settings")
    .select("key,value");
  if (error || !data) return map;
  for (const row of data) {
    const v = (row as any).value;
    const num = typeof v === "number" ? v : Number(v);
    if (Number.isFinite(num)) map.set(row.key, num);
  }
  return map;
}

export async function upsertPricingSettings(
  entries: Array<{ key: string; value: number }>
): Promise<void> {
  if (entries.length === 0) return;
  const rows = entries.map((e) => ({ key: e.key, value: e.value as any }));
  const { error } = await supabase
    .from("pricing_settings")
    .upsert(rows, { onConflict: "key" });
  if (error) throw error;
  // Apply locally so the rest of the app sees the change immediately.
  for (const e of entries) applyRow(e.key, e.value);
}

export async function deletePricingSettings(keys: string[]): Promise<void> {
  if (keys.length === 0) return;
  const { error } = await supabase
    .from("pricing_settings")
    .delete()
    .in("key", keys);
  if (error) throw error;
}
